import type {
  AiOperationsRun,
  AiOperationsRunAction,
  AiRunDisplayStatus,
  AiRunIncident,
  AiRunPrimaryOutcome,
  ApprovalProposalDetail,
} from "@adpilot/shared";

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type RunOutcomeKey = AiRunPrimaryOutcome;

interface AiRunDisplayStatusContext {
  actions?: readonly AiOperationsRunAction[];
  proposalActionCount?: number;
  review?: Pick<
    ApprovalProposalDetail,
    "actions" | "executionStatus" | "finalizationStatus" | "reviewStatus" | "routingStatus"
  >;
}

function rollbackFailed(actions: readonly AiOperationsRunAction[]): boolean {
  return actions.some(
    ({ rollbackOfActionId, status }) =>
      rollbackOfActionId !== null && (status === "failed" || status === "skipped"),
  );
}

function incidentStatus(
  run: AiOperationsRun,
  context: AiRunDisplayStatusContext,
): AiRunIncident | null {
  if (rollbackFailed(context.actions ?? [])) return "rollbackFailed";
  if (
    context.review?.executionStatus === "failed" ||
    context.review?.executionStatus === "partially_failed" ||
    context.review?.actions.some(({ executionStatus }) => executionStatus === "failed") ||
    run.failed > 0
  ) {
    return "executionFailed";
  }
  if (context.review?.finalizationStatus === "failed") return "finalizationFailed";
  if (run.status !== "failed") return null;
  // A persisted human outcome with no failed Action means the business
  // decision succeeded and the Run failed while closing its workflow record.
  if (run.rejectedActions > 0 || run.executedActions > 0) return "finalizationFailed";
  return "unknownFailure";
}

function detailedOutcome(
  run: AiOperationsRun,
  context: AiRunDisplayStatusContext,
): AiRunPrimaryOutcome | null {
  const review = context.review;
  const actionCount = context.proposalActionCount ?? run.candidateActions;
  if (review?.finalizationStatus === "processing") return "finalizing";
  if (review?.reviewStatus === "pending" || run.status === "suspended") return "needsDecision";
  if (run.status === "running") return "analyzing";
  if (run.rolledBackActions > 0) return "rolledBack";
  if (review?.reviewStatus === "mixed" || review?.executionStatus === "partially_failed") {
    return "partial";
  }
  if (review?.reviewStatus === "rejected") {
    return review.executionStatus === "not_applicable" ? "rejected" : "partial";
  }
  if (review?.reviewStatus === "expired") return "expired";
  if (review?.executionStatus === "executing") return "executing";
  if (review?.executionStatus === "queued") return "approvedPendingExecution";
  if (review?.executionStatus === "succeeded") return "executed";
  if (review?.reviewStatus === "approved") return "approved";
  if (review?.routingStatus === "recommendation") return "recommended";
  if (actionCount === 0 && run.status !== "failed") return "noChanges";
  return null;
}

function listOutcome(run: AiOperationsRun, hasDecision: boolean): AiRunPrimaryOutcome | null {
  if (hasDecision || run.status === "suspended") return "needsDecision";
  if (run.status === "running") return "analyzing";
  if (run.rolledBackActions > 0) return "rolledBack";
  if (
    run.status === "partial" &&
    run.jobType === "daily_search_term_mining" &&
    run.candidateActions > 0
  ) {
    return "partialRecommended";
  }
  if (run.status === "partial") return "partial";
  if (run.approvalCreated > 0) {
    if (run.rejectedActions > 0 && (run.executedActions > 0 || run.failed > 0)) return "partial";
    if (run.rejectedActions >= run.approvalCreated) return "rejected";
    if (run.rejectedActions > 0) return "partial";
    if (run.skipped >= run.approvalCreated && run.executedActions === 0) return "expired";
    if (run.executedActions > 0 || run.autoExecuted > 0) return "executed";
    if (run.failed > 0) return "approved";
    if (run.detailAvailability === "legacy_list_only") return "recommended";
    if (run.status === "success") return "expired";
  }
  if (run.executedActions > 0 || run.autoExecuted > 0) return "executed";
  if (run.status === "success" && run.candidateActions > 0) return "recommended";
  if (run.status === "success" && run.jobType === "daily_bid_optimization") return "noChanges";
  if (run.status === "success") return "completed";
  return null;
}

/** Project orthogonal persisted states into one primary outcome plus an incident. */
export function runDisplayStatus(
  run: AiOperationsRun,
  hasDecision: boolean,
  context: AiRunDisplayStatusContext = {},
): AiRunDisplayStatus {
  const detailed = context.review !== undefined || context.proposalActionCount !== undefined;
  const outcome = detailed ? detailedOutcome(run, context) : listOutcome(run, hasDecision);
  // 拒绝是正常的人工终态。底层仍保留技术状态用于日志排查，但用户界面
  // 不把拒绝后的工作流收尾问题解释成异常或失败。
  const incident = outcome === "rejected" ? null : incidentStatus(run, context);
  return { outcome: outcome ?? (incident === null ? "observed" : null), incident };
}

export interface FinalizedRunPresentation {
  description: string;
  title: string;
  tone: "danger" | "neutral" | "success" | "warning";
}

export function finalizedRunPresentation(
  status: AiRunDisplayStatus,
  t: TranslateFn,
): FinalizedRunPresentation {
  const incident = status.outcome === "rejected" ? null : status.incident;
  const title = [
    status.outcome ? t(`history.outcome.${status.outcome}`) : null,
    incident ? t(`history.incident.${incident}`) : null,
  ]
    .filter((label): label is string => label !== null)
    .join(" · ");
  const description = incident
    ? t(`runDetail.decisionRecord.finalizing.incident.${incident}Description`)
    : status.outcome === "rejected"
      ? t("runDetail.decisionRecord.finalizing.rejectedDescription")
      : status.outcome === "expired"
        ? t("runDetail.decisionRecord.finalizing.expiredDescription")
        : status.outcome === "partial"
          ? t("runDetail.decisionRecord.finalizing.partialDescription")
          : status.outcome === "finalizing"
            ? t("runDetail.decisionRecord.finalizing.processingDescription")
            : status.outcome === "approvedPendingExecution" || status.outcome === "executing"
              ? t("runDetail.decisionRecord.finalizing.executingDescription")
              : t("runDetail.decisionRecord.finalizing.completeDescription");
  const tone = incident
    ? "danger"
    : status.outcome === "finalizing" ||
        status.outcome === "executing" ||
        status.outcome === "approvedPendingExecution" ||
        status.outcome === "expired" ||
        status.outcome === "partial"
      ? "warning"
      : status.outcome === "rejected"
        ? "neutral"
        : "success";
  return { description, title, tone };
}

const OUTCOME_CLASS: Record<RunOutcomeKey, string> = {
  needsDecision: "text-amber-11",
  analyzing: "text-amber-11",
  finalizing: "text-amber-11",
  approvedPendingExecution: "text-amber-11",
  approved: "text-green-11",
  noChanges: "text-green-11",
  executing: "text-amber-11",
  partial: "text-amber-11",
  executed: "text-green-11",
  rolledBack: "text-muted-foreground",
  rejected: "text-muted-foreground",
  expired: "text-amber-11",
  recommended: "text-brand",
  partialRecommended: "text-amber-11",
  completed: "text-green-11",
  observed: "text-muted-foreground",
};

export function runOutcome(
  run: AiOperationsRun,
  hasDecision: boolean,
  t: TranslateFn,
): { label: string; className: string } {
  const status = runDisplayStatus(run, hasDecision);
  const labels = [
    status.outcome ? t(`history.outcome.${status.outcome}`) : null,
    status.incident ? t(`history.incident.${status.incident}`) : null,
  ].filter((label): label is string => label !== null);
  return {
    label: labels.join(" · "),
    className: status.incident ? "text-red-11" : OUTCOME_CLASS[status.outcome ?? "observed"],
  };
}

export function formatRunFailureReason(
  reason: string,
  autoExecuted: number,
  t: TranslateFn,
): string {
  if (reason.includes("finished before submitting an accepted Proposal")) {
    return t("runDetail.failed.noAcceptedProposal");
  }
  if (autoExecuted > 0) {
    return t("runDetail.failed.genericAfterAutoExecution", { count: autoExecuted });
  }
  return t("runDetail.failed.generic");
}

export function runResultDetail(
  run: AiOperationsRun,
  pendingDecisions: number,
  t: TranslateFn,
): string {
  if (pendingDecisions > 0) {
    return t("history.resultDetail.proposedChanges", { count: run.candidateActions });
  }
  const status = runDisplayStatus(run, false);
  if (status.outcome === "rejected") return t("history.resultDetail.rejected");
  if (status.incident === "executionFailed") {
    return t("history.resultDetail.executionFailed");
  }
  if (status.incident === "finalizationFailed") {
    return t("history.resultDetail.finalizationFailed");
  }
  if (status.incident === "rollbackFailed") return t("history.resultDetail.rollbackFailed");
  if (status.incident === "unknownFailure") return t("history.resultDetail.unknownFailure");
  if (run.jobType === "daily_search_term_mining" && run.candidateActions > 0) {
    return t("history.resultDetail.keywordSuggestions", { count: run.candidateActions });
  }
  if (run.failureReason) return formatRunFailureReason(run.failureReason, run.autoExecuted, t);
  if (run.candidateActions === 0) return t("history.resultDetail.insufficientSignal");
  return run.failed > 0
    ? t("history.resultDetail.findingsFailed", {
        findings: run.candidateActions,
        failed: run.failed,
      })
    : t("history.resultDetail.findingsPassed", { findings: run.candidateActions });
}
